// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ShieldCheck } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { FacilitySwitcher } from "@/components/facility/FacilitySwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useFacility } from "@/contexts/facility/FacilityContext";
import { useIsBlessedAdmin } from "@/hooks/useIsBlessedAdmin";
import { useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { PRIMARY_NAV, ACCOUNT_NAV } from "@/config/appNavigation";
import { filterNavItemsForRole, isPlatformAdmin } from "@/lib/platformAdmin";
import { FREE_TIER_HOME_PATH } from "@/lib/freeTierAccess";
import { tierRank } from "@/lib/effectiveAccess";
import type { NavItem } from "@/types/navigation";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { cn } from "@/lib/utils";
import { resolveUiSkin } from "@/lib/uiSkin";
import { getBrandAssets } from "@/lib/brandAssets";
import { NavLink } from "react-router-dom";

const FACILITY_ADMIN_NAV: NavItem = {
  id: "facility-admin",
  label: "Facility Administration",
  path: "/facility/admin",
  icon: ShieldCheck,
  requiredTier: "free",
  match: "prefix",
};

export function AppSidebar() {
  const { profile } = useAuth();
  const { activeFacility } = useFacility();
  const { access } = useEffectiveAccess();
  const { isBlessedAdmin } = useIsBlessedAdmin(activeFacility?.id);
  const [logoError, setLogoError] = useState(false);
  const isAthenaSkin = resolveUiSkin(profile?.id) === "athena";
  const brand = getBrandAssets(isAthenaSkin ? "athena" : "classic");
  const moduleNav = filterNavItemsForRole(
    PRIMARY_NAV,
    isPlatformAdmin(profile?.role),
  );
  const ceiling = access?.is_member ? access.effective_tier : profile?.membership_tier;
  const homePath =
    isPlatformAdmin(profile?.role) || tierRank(ceiling) > 0
      ? "/dashboard"
      : FREE_TIER_HOME_PATH;

  return (
    <Sidebar
      collapsible="icon"
    >
      <SidebarHeader className="border-b border-sidebar-border p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip="Home">
              <NavLink to={homePath}>
                {logoError ? (
                  <div
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-md",
                      isAthenaSkin ? "bg-neutral-800" : "bg-primary/15 text-primary",
                    )}
                  >
                    <Flame className="h-4 w-4" />
                  </div>
                ) : (
                  <img
                    src={brand.logoMark}
                    alt=""
                    className={cn("h-8 w-8 object-contain", isAthenaSkin && "invert")}
                    onError={() => setLogoError(true)}
                  />
                )}
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-heading font-light tracking-tight">
                    {isAthenaSkin ? "Athena Ag" : "Camp Promethean"}
                  </span>
                  <span className="truncate text-xs text-primary/70">Workspace</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {profile && (
          <div className="mt-2 px-1">
            <FacilitySwitcher />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Modules</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {moduleNav.map((item) => (
                <SidebarNavItem key={item.id} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {ACCOUNT_NAV.map((item) => (
                <SidebarNavItem key={item.id} item={item} />
              ))}
              {/* RB-8: blessed admins (and only they) see their facility's admin surface. */}
              {isBlessedAdmin && (
                <SidebarNavItem item={FACILITY_ADMIN_NAV} />
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarUserMenu />
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
